import { Suspense, useState } from 'react';
import { Skeleton, Typography } from '@mui/material';
import { type ActionButtonType } from 'further-ui/components/ActionButtons';
import { Column } from 'further-ui/components/Layout';
import { CalendarDay, downloadFileFromTrpc } from 'further-ui/utils';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useNotification } from 'hooks/ui/useNotification';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { CurrencySettings } from '../../types';
import PcapDateSelector from './PcapDateSelector';
import PcapTable from './PcapTable';

type Props = {
  investorId: string;
  commitmentId?: string;
  vehicleId: string;
  currencySettings: CurrencySettings;
  investorName: string;
  closeDate?: CalendarDay;
  earliestCloseDate?: CalendarDay;
};

const getMinCloseDate = ({
  commitmentId,
  closeDate,
  earliestCloseDate,
}: {
  commitmentId?: string;
  closeDate?: CalendarDay;
  earliestCloseDate?: CalendarDay;
}) => (commitmentId ? closeDate : earliestCloseDate);

const PcapContent: React.FC<
  Omit<Props, 'closeDate' | 'earliestCloseDate'> & {
    selectedDate: CalendarDay;
    vehicleName?: string;
  }
> = ({
  investorId,
  commitmentId,
  vehicleId,
  currencySettings,
  investorName,
  vehicleName,
  selectedDate,
}) => {
  const [pcapData] = api.generalLedger.getPcap.useSuspenseQuery({
    vehicleId,
    investorId,
    ...(commitmentId ? { commitmentId } : {}),
    endDate: selectedDate,
  });

  if (!pcapData) {
    return (
      <Typography color="textSecondary">
        The PCAP will only display after this commitment has been included in a
        fund close.
      </Typography>
    );
  }

  return (
    <PcapTable
      pcap={pcapData}
      currency={currencySettings.currencyLcy}
      investorName={investorName}
      vehicleName={vehicleName}
    />
  );
};

type PcapSectionContentProps = Props & {
  initialDate: CalendarDay;
};

const PcapSectionContent: React.FC<PcapSectionContentProps> = ({
  investorId,
  commitmentId,
  vehicleId,
  currencySettings,
  investorName,
  initialDate,
  closeDate,
  earliestCloseDate,
}) => {
  const { vehicle } = useLayoutContext();
  const { error } = useNotification();
  const [canDownloadPcap] = useHasPermissions([
    RolePermission.LpReadCommitment,
  ]);
  const [selectedDate, setSelectedDate] = useState(initialDate);

  const { data: pcapData } = api.generalLedger.getPcap.useQuery({
    vehicleId,
    investorId,
    ...(commitmentId ? { commitmentId } : {}),
    endDate: selectedDate,
  });

  const { mutateAsync: handleDownload, isPending } =
    api.generalLedger.downloadPcap.useMutation(
      useHandleTrpcMutation()({ invalidations: [] }),
    );

  const handleDownloadPcap = async () => {
    const data = await handleDownload({
      vehicleId,
      investorId,
      ...(commitmentId ? { commitmentId } : {}),
      endDate: selectedDate,
    });
    downloadFileFromTrpc(data, () => error('Failed to download PCAP.'));
  };

  const filterActions: Array<ActionButtonType> =
    pcapData && canDownloadPcap
      ? [
          {
            label: 'Download PCAP',
            onClick: handleDownloadPcap,
            loading: isPending,
            variant: 'contained',
          },
        ]
      : [];

  return (
    <Column spacing="md" fullWidth>
      <PageFilters actions={filterActions}>
        <Filter size="sm">
          <PcapDateSelector
            selectedDate={selectedDate}
            onChange={setSelectedDate}
            minDate={getMinCloseDate({
              commitmentId,
              closeDate,
              earliestCloseDate,
            })}
          />
        </Filter>
      </PageFilters>
      <Suspense fallback={<Skeleton height={400} />}>
        <PcapContent
          investorId={investorId}
          commitmentId={commitmentId}
          vehicleId={vehicleId}
          currencySettings={currencySettings}
          investorName={investorName}
          vehicleName={vehicle?.vehicleName}
          selectedDate={selectedDate}
        />
      </Suspense>
    </Column>
  );
};

const PcapSection: React.FC<Props> = (props) => {
  const [periods] = api.accountingPeriod.listPeriods.useSuspenseQuery({
    vehicleId: props.vehicleId,
  });

  if (!periods.length) return null;

  const minCloseDate = getMinCloseDate({
    commitmentId: props.commitmentId,
    closeDate: props.closeDate,
    earliestCloseDate: props.earliestCloseDate,
  });
  const initialDate =
    periods.find(
      (period) => !minCloseDate || period.periodEnd.isSameOrAfter(minCloseDate),
    )?.periodEnd ?? periods[0].periodEnd;

  return (
    <PcapSectionContent
      key={props.commitmentId ?? 'all'}
      {...props}
      initialDate={initialDate}
    />
  );
};

export default PcapSection;
